/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { isPresent } from '@ember/utils';

import MessageLibraryItemModel, {
  MessageLibraryItemDeliveryType,
  MessageLibraryItemStatus,
  MatchedField,
} from 'cio/models/message-library-item';
import AnalyticsService from 'cio/services/analytics';

const DELIVERY_TYPE_ICON_MAP: Record<MessageLibraryItemDeliveryType, string> = {
  [MessageLibraryItemDeliveryType.Email]: 'email',
  [MessageLibraryItemDeliveryType.Push]: 'mobile',
  [MessageLibraryItemDeliveryType.Slack]: 'slack',
  [MessageLibraryItemDeliveryType.Twilio]: 'sms',
  [MessageLibraryItemDeliveryType.Unset]: 'newsletter',
  [MessageLibraryItemDeliveryType.Webhook]: 'webhook',
  [MessageLibraryItemDeliveryType.CreateEvent]: 'event',
  [MessageLibraryItemDeliveryType.AttributeUpdate]: 'person',
  [MessageLibraryItemDeliveryType.BatchUpdate]: 'person',
  [MessageLibraryItemDeliveryType.InApp]: 'in-app',
};

interface MessageLibraryItemsTableRowArgs {
  message: MessageLibraryItemModel;
  showUpdatedAtColumn: boolean;
  query?: string;
}

export default class MessageLibraryItemsTableRow extends Component<MessageLibraryItemsTableRowArgs> {
  @service analytics!: AnalyticsService;

  get showUpdatedAtColumn() {
    return this.args.showUpdatedAtColumn ?? true;
  }

  get icon() {
    return DELIVERY_TYPE_ICON_MAP[this.args.message.deliveryType];
  }

  get messageRoute() {
    if (this.args.message.belongsToCampaign) {
      return 'composer.action';
    } else if (this.args.message.belongsToNewsletter) {
      return this.args.message.status === MessageLibraryItemStatus.Sent
        ? 'broadcasts.newsletter.overview.content'
        : 'composer.newsletter.template';
    } else if (this.args.message.belongsToTransactional) {
      return 'composer.transactional';
    } else {
      return undefined;
    }
  }

  get messageRouteModels() {
    let {
      belongsToCampaign,
      belongsToNewsletter,
      belongsToTransactional,
      actionId,
      newsletterId,
      templateId,
      transactionalMessageId,
    } = this.args.message;

    if (belongsToCampaign) {
      return [actionId];
    } else if (belongsToNewsletter) {
      return this.args.message.status === MessageLibraryItemStatus.Sent ? [newsletterId] : [newsletterId, templateId];
    } else if (belongsToTransactional) {
      return [transactionalMessageId];
    } else {
      return [];
    }
  }

  get showHighlight() {
    return isPresent(this.args.query) && isPresent(this.args.message.highlight);
  }

  get matchItems() {
    return [
      {
        label: 'from',
        value: this.args.message.fromAddress,
        isVisible: this.args.message.fieldsMatched?.includes(MatchedField.FromAddress),
      },
      {
        label: 'reply to',
        value: this.args.message.replyTo,
        isVisible: this.args.message.fieldsMatched?.includes(MatchedField.ReplyTo),
      },
      {
        label: 'bcc',
        value: this.args.message.bcc,
        isVisible: this.args.message.fieldsMatched?.includes(MatchedField.BCC),
      },
      {
        label: 'subject',
        value: this.args.message.subject,
        isVisible: isPresent(this.args.message.subject),
      },
    ];
  }

  get hasVisibleMatchItems() {
    return this.matchItems.some((item) => item.isVisible);
  }

  get linkQuery() {
    if (this.args.message.belongsToNewsletter && this.args.message.status === MessageLibraryItemStatus.Sent) {
      return this.args.message.templateId;
    }
    return null;
  }

  @action
  trackMessageSelected() {
    this.analytics.track('library', 'message_selected');
  }
}
